import { clamp } from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { m, type MithrilVNode } from '../../mithril';
import { ImageButton } from '../button/image.button';
import { MaterialIcon } from '../icon/icons';
import { IMithrilStateComponent } from '../mithril.component';
import { addStyles, DefaultTheme } from '../theme';
import { WHITE } from '../theme.palette';

const CSS = {
	container: 'c-Carousel',
	constraint: 'c-Carousel__Constraint',
	controls: 'c-Carousel__Controls',
	track: 'c-Carousel__Track',
	center: 'c-Carousel__Center',
	image: 'c-Carousel__Image',
	dot: 'c-Carousel__Dot',
	highlighted: 'c-Carousel__Dot--highlighted',
};

const Style = {
	[CSS.container]: {
		position: 'relative',
		width: '100%',
		height: '100%',
	},

	[CSS.controls]: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		zIndex: 1,
		pointerEvents: 'none',
	},

	[`${CSS.controls} a`]: {
		pointerEvents: 'all',
		backgroundColor: DefaultTheme.background.mediumGrey,
		color: `rgb(${WHITE})`,

		'&:hover': {
			backgroundColor: DefaultTheme.background.darkGrey,
		},

		'&:disabled': {
			backgroundColor: DefaultTheme.background.mediumGrey,
			color: `rgb(${WHITE})`,
			opacity: '0.4',
		},
	},

	[CSS.constraint]: {
		overflow: 'hidden',
		position: 'relative',
		height: 'calc(100% - 18px)',
		paddingBottom: '6px',
	},

	[CSS.track]: {
		height: '100%',
		transition: '150ms ease',
		display: 'flex',
		flexWrap: 'nowrap',
	},

	[CSS.center]: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
	},

	[CSS.image]: {
		height: '100%',
	},

	[CSS.dot]: {
		width: '12px',
		height: '12px',
		borderRadius: '6px',
		background: DefaultTheme.background.lightGrey,
		cursor: 'pointer',
		border: 0,
		margin: '0 2px',
	},

	[CSS.highlighted]: {
		background: DefaultTheme.background.mediumGrey,
	},
};

addStyles(Style);

interface ICarouselProps {
	index?: number;
	slides: MithrilVNode[];
	showControls?: boolean;
	onSlideChange?: (index: number) => void;
}

export class Carousel extends IMithrilStateComponent {
	@observable currentIndex: number | null = null;

	slides: MithrilVNode[];
	showControls: boolean;

	onSlideChange: (index: number) => void;

	constructor({ index = 0, slides = [], showControls = true, onSlideChange }: ICarouselProps) {
		super();

		makeObservable(this);

		this.currentIndex = clamp(index, 0, slides.length - 1);
		this.slides = slides;
		this.showControls = showControls;
		this.onSlideChange = onSlideChange;
	}

	@action goTo(index: number) {
		this.currentIndex = clamp(index, 0, this.slides.length - 1);
		this.onSlideChange?.(this.currentIndex);
	}

	get scalarSlideWidth() {
		// avoid NaN from 0 denominator
		if (this.slides.length > 0) {
			return 100 / this.slides.length;
		}

		return 0;
	}

	get controls() {
		if (this.showControls) {
			return m(
				'div',
				{
					className: CSS.controls,
				},
				[
					ImageButton.view(MaterialIcon.NAVIGATE_PREV, {
						onclick: () => this.goTo(this.currentIndex - 1),
						disabled: this.currentIndex === 0,
					}),
					ImageButton.view(MaterialIcon.NAVIGATE_NEXT, {
						onclick: () => this.goTo(this.currentIndex + 1),
						disabled: this.currentIndex === this.slides.length - 1 || !this.slides.length,
					}),
				]
			);
		}
		return null;
	}

	get dots() {
		return m(
			'div',
			{
				className: CSS.center,
			},
			this.slides.map((_, i) =>
				m('button', {
					className: `${CSS.dot} ${i === this.currentIndex && CSS.highlighted}`,
					onclick: () => this.goTo(i),
				})
			)
		);
	}

	get nodes() {
		return m(
			'div',
			{
				className: CSS.container,
			},
			[
				this.controls,
				m(
					'div',
					{
						className: CSS.constraint,
					},
					m(
						'div',
						{
							className: CSS.track,
							style: {
								width: `${100 * this.slides.length}%`,
								transform: `translateX(-${this.currentIndex * this.scalarSlideWidth}%)`,
							},
						},
						this.slides.map((slide) =>
							m(
								'div',
								{
									className: `${CSS.center} ${CSS.image}`,
								},
								slide
							)
						)
					)
				),
				this.dots,
			]
		);
	}
}
